// Pure projection of the objectives payload onto the four scorecard numbers a
// manager reads first. Kept free of React so the "which number, which colour"
// rules can be unit-tested directly.
import { pct, ratioTone, fmtCount, RATIO_TONES, SEGMENT_COLORS } from './objectiveFormat';

export const OBJECTIVE_TAB_KEYS = {
  schedule:   'schedule',
  orderEntry: 'order-entry',
  jafEst:     'jaf-est',
};

const ON_TIME_KEY = 'earlier_than_2_days';

const scheduleCards = (scheduleSummary) => {
  const headline = scheduleSummary?.groups?.find((group) => group.cse === 'ALL')?.headline;
  const jobs = fmtCount(scheduleSummary?.totals?.jobs);

  return [
    {
      key:    'meet-csd',
      tab:    OBJECTIVE_TAB_KEYS.schedule,
      label:  'Meet CSD schedule',
      value:  pct(headline?.meet_schedule_ratio, 1),
      hint:   `ENG release ≤ CSD request · avg APD/SHOP/ERD · ${jobs} jobs`,
      // Runs at 13–20% in practice — a 90% grade would paint it red forever.
      tone:   RATIO_TONES.neutral,
      accent: SEGMENT_COLORS.primary,
      bar:    headline?.meet_schedule_ratio ?? 0,
    },
    {
      key:    'meet-committed',
      tab:    OBJECTIVE_TAB_KEYS.schedule,
      label:  'Meet committed schedule',
      value:  pct(headline?.meet_committed_ratio, 1),
      hint:   'ENG release ≤ date ENG committed · target 90%',
      tone:   ratioTone(headline?.meet_committed_ratio),
      accent: ratioTone(headline?.meet_committed_ratio),
      bar:    headline?.meet_committed_ratio ?? 0,
    },
  ];
};

const orderEntryCard = (oeSummary) => {
  const onTime = oeSummary?.buckets?.find((bucket) => bucket.key === ON_TIME_KEY);
  return {
    key:    'oe-on-time',
    tab:    OBJECTIVE_TAB_KEYS.orderEntry,
    label:  'Order entry ≤ 2 days',
    value:  pct(onTime?.ratio, 1),
    hint:   `${fmtCount(onTime?.count)} of ${fmtCount(oeSummary?.total)} jobs entered on time`,
    tone:   ratioTone(onTime?.ratio),
    accent: ratioTone(onTime?.ratio),
    bar:    onTime?.ratio ?? 0,
  };
};

const jafEstCard = (jafEstSummary) => {
  const summary = jafEstSummary?.summary;
  const overrun = summary?.jaf_lt_est?.count ?? 0;
  return {
    key:    'jaf-gte-est',
    tab:    OBJECTIVE_TAB_KEYS.jafEst,
    label:  'JAF ≥ EST',
    value:  pct(summary?.jaf_gte_est?.ratio, 1),
    hint:   overrun > 0 ? `${fmtCount(overrun)} jobs overrun contract weight` : 'No job overruns contract weight',
    tone:   ratioTone(summary?.jaf_gte_est?.ratio),
    accent: ratioTone(summary?.jaf_gte_est?.ratio),
    bar:    summary?.jaf_gte_est?.ratio ?? 0,
  };
};

export const buildScorecard = (objectives) => [
  ...scheduleCards(objectives?.schedule_summary),
  orderEntryCard(objectives?.oe_summary),
  jafEstCard(objectives?.jaf_est_summary),
];

export const scopeLabel = (objectives) => {
  const totals = objectives?.schedule_summary?.totals;
  if (!totals) return '';
  return `${fmtCount(totals.jobs)} jobs · ${fmtCount(totals.change_orders)} change orders`;
};
